import { Link as RouterLink } from "react-router-dom";
import Container from "@mui/material/Container";
import Grid from "@mui/material/Grid";
import { Zap, Phone, Mail, MapPin } from "lucide-react";
import { PRODUCTS, COMPANY } from "../data/products";
import { useState } from "react";
import {
  Box,
  Stack,
  Typography,
  Link,
  Modal,
  ModalDialog,
  ModalClose,
} from "@mui/joy";
// Theme color tokens (mirrors the original Tailwind "obsidian" / "safety" custom colors)
const COLORS = {
  obsidian: "#0b0b0c",
  safety: "#ff6a00",
  white: "#ffffff",
};

export function Footer() {
  const [openTerms, setOpenTerms] = useState(false);
  return (
    <Box
      component="footer"
      data-testid="site-footer"
      sx={{
        backgroundColor: COLORS.obsidian,
        color: COLORS.white,
        position: "relative",
        overflow: "hidden", // Prevents noise texture from clipping weirdly on tiny screens
        // "grain grain-dark" texture overlay
        "&::before": {
          content: '""',
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.06,
          backgroundImage:
            "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          mixBlendMode: "overlay",
        },
      }}
    >
      <Container
        maxWidth="xl"
        sx={{
          position: "relative",
          mx: "auto",
          px: { xs: 3, sm: 4, md: 6 }, // Gradual padding growth across all form factors
          py: { xs: 6, sm: 8, md: 12 },
        }}
      >
        {/* Main Links Grid */}
        <Grid container spacing={{ xs: 4, sm: 6, md: 8, lg: 12 }} columns={12}>
          {/* Brand / About */}
          <Grid item xs={12} md={4}>
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mb: 3 }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  display: "grid",
                  placeItems: "center",
                  backgroundColor: COLORS.safety,
                  color: COLORS.white,
                }}
              >
                <Zap size={20} strokeWidth={2.5} />
              </Box>
              <Typography
                component="span"
                sx={{
                  fontFamily: "var(--font-display, inherit)",
                  fontWeight: 800,
                  fontSize: "1.125rem",
                  letterSpacing: "-0.02em",
                }}
              >
                ITL Technical Service
              </Typography>
            </Stack>
            <Typography
              sx={{
                color: "rgba(255,255,255,0.6)",
                fontSize: "0.875rem",
                lineHeight: 1.6,
                textAlign: "left",
                maxWidth: { xs: "100%", md: 384 }, // Full width on small screens, bounded on desktop
              }}
            >
              Precision-engineered power conditioning equipment. Manufacturer of
              servo voltage stabilizers, isolation transformers and CVTs — built
              to Indian standards for uncompromising industrial reliability.
            </Typography>
          </Grid>

          {/* Products */}
          <Grid item xs={6} sm={4} md={3}>
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: COLORS.safety,
                mb: 3,
                textAlign: "left",
              }}
            >
              Products
            </Typography>
            <Stack
              component="ul"
              spacing={1.5}
              sx={{ listStyle: "none", p: 0, m: 0, alignItems: "flex-start" }}
            >
              {PRODUCTS.map((p) => (
                <Box component="li" key={p.slug}>
                  <Link
                    component={RouterLink}
                    to={`/products/${p.slug}`}
                    data-testid={`footer-product-${p.slug}`}
                    underline="none"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "0.875rem",
                      transition: "color 0.2s",
                      "&:hover": { color: COLORS.white },
                    }}
                  >
                    {p.short}
                  </Link>
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Company */}
          <Grid item xs={6} sm={4} md={2}>
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: COLORS.safety,
                mb: 3,
                textAlign: "left",
              }}
            >
              Company
            </Typography>
            <Stack
              component="ul"
              spacing={1.5}
              sx={{ listStyle: "none", p: 0, m: 0, alignItems: "flex-start" }}
            >
              {[
                ["About", "/about"],
                ["Services", "/services"],
                ["Industries", "/industries"],
                ["Get a Quote", "/contact"],
              ].map(([l, to]) => (
                <Box component="li" key={to}>
                  <Link
                    component={RouterLink}
                    to={to}
                    underline="none"
                    sx={{
                      color: "rgba(255,255,255,0.7)",
                      fontSize: "0.875rem",
                      transition: "color 0.2s",
                      "&:hover": { color: COLORS.white },
                    }}
                  >
                    {l}
                  </Link>
                </Box>
              ))}
            </Stack>
          </Grid>

          {/* Contact */}
          <Grid item xs={12} sm={4} md={3}>
            <Typography
              sx={{
                fontFamily: "monospace",
                fontSize: "0.75rem",
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                color: COLORS.safety,
                mb: 3,
                textAlign: "left",
              }}
            >
              Contact
            </Typography>
            <Stack
              component="ul"
              spacing={1.5}
              sx={{ listStyle: "none", p: 0, m: 0, alignItems: "flex-start" }}
            >
              <Stack
                component="li"
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
              >
                <Phone
                  size={16}
                  style={{ marginTop: 2, color: COLORS.safety, flexShrink: 0 }}
                />
                <Link
                  href={`tel:${COMPANY.phone}`}
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    "&:hover": { color: COLORS.white },
                  }}
                >
                  {COMPANY.phone}
                </Link>
              </Stack>
              <Stack
                component="li"
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
              >
                <Mail
                  size={16}
                  style={{ marginTop: 2, color: COLORS.safety, flexShrink: 0 }}
                />
                <Link
                  href={`mailto:${COMPANY.email}`}
                  underline="none"
                  sx={{
                    color: "rgba(255,255,255,0.7)",
                    wordBreak: "break-all",
                    "&:hover": { color: COLORS.white },
                  }}
                >
                  {COMPANY.email}
                </Link>
              </Stack>
              <Stack
                component="li"
                direction="row"
                spacing={1.5}
                alignItems="flex-start"
              >
                <MapPin
                  size={16}
                  style={{ marginTop: 2, color: COLORS.safety, flexShrink: 0 }}
                />
                <Typography
                  component="span"
                  sx={{ fontSize: "0.875rem", color: "rgba(255,255,255,0.7)" }}
                >
                  {COMPANY.address}
                </Typography>
              </Stack>
            </Stack>
          </Grid>
        </Grid>

        {/* Bottom bar */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={3} // Replaced layout-breaking "60" with clean fluid layout spacing
          sx={{
            mt: { xs: 6, md: 8 },
            pt: 4,
            borderTop: "1px solid rgba(255,255,255,0.1)",
            width: "100%",
          }}
        >
          <Stack direction="row" flexWrap="wrap" useFlexGap sx={{ gap: 1.25 }}>
            {[
              "BIS / ISI CERTIFIED",
              "ISO 9001:2015",
              "MADE IN INDIA",
              "CUSTOM BUILT",
            ].map((c) => (
              <Box
                component="span"
                key={c}
                sx={{
                  fontFamily: "monospace",
                  fontSize: "10px",
                  letterSpacing: "0.15em",
                  border: "1px solid rgba(255,255,255,0.2)",
                  px: 1.5,
                  py: 0.75,
                  color: "rgba(255,255,255,0.7)",
                }}
              >
                {c}
              </Box>
            ))}
          </Stack>

          <Typography
            sx={{
              fontFamily: "monospace",
              fontSize: "0.75rem",
              color: "rgba(255,255,255,0.4)",
            }}
          >
            © {new Date().getFullYear()} ITL Technical Services. All rights
            reserved.
            <Link
              component="button"
              underline="hover"
              onClick={() => setOpenTerms(true)}
              sx={{
                color: "#ff8c42",
                fontSize: "0.75rem",
                fontFamily: "monospace",
                cursor: "pointer",
                ml: 1,
              }}
            >
              Terms & Conditions
            </Link>
          </Typography>
        </Stack>
      </Container>
      <Modal open={openTerms} onClose={() => setOpenTerms(false)}>
        <ModalDialog
          layout="center"
          sx={{
            width: { xs: "95%", md: 850 },
            maxHeight: "88vh",
            overflowY: "auto",
            bgcolor: "#ffffff",
            borderRadius: 0,
            border: "1px solid rgba(22,22,22,0.12)",
            p: 0,
            boxShadow: "0 30px 80px rgba(0,0,0,0.18)",
          }}
        >
          <ModalClose sx={{ m: 2 }} />

          {/* Header */}
          <Box
            sx={{
              px: { xs: 3, md: 6 },
              py: 5,
              borderBottom: "1px solid rgba(22,22,22,.08)",
            }}
          >
            <Typography
              sx={{
                fontFamily: "monospace",
                fontWeight: 600,
                letterSpacing: ".08em",
                textTransform: "uppercase",
                color: "#ff5a1f",
                fontSize: ".9rem",
                mb: 2,
              }}
            >
              [ Legal ]
            </Typography>

            <Typography
              sx={{
                fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                fontWeight: 900,
                fontSize: { xs: "2.3rem", md: "3rem" },
                letterSpacing: "-0.04em",
                lineHeight: 0.9,
                color: "#161616",
              }}
            >
              Terms & Conditions
            </Typography>
          </Box>

          {/* Content */}
          <Box
            sx={{
              px: { xs: 3, md: 6 },
              py: 5,
            }}
          >
            <Typography
              sx={{
                fontSize: "1.1rem",
                lineHeight: 1.8,
                color: "rgba(22,22,22,.65)",
                mb: 5,
              }}
            >
              Please read these Terms & Conditions carefully before using ITL
              Technical Services' website or requesting our products and
              services.
            </Typography>

            {[
              {
                title: "1. Products & Services",
                body: "All specifications, dimensions, ratings and product information displayed on this website are subject to change without prior notice. Product availability depends on manufacturing schedules and customer requirements.",
              },
              {
                title: "2. Quotations",
                body: "Quotations issued by ITL Technical Services remain valid only for the period specified. Final pricing may vary depending on technical specifications, quantity, taxes and delivery requirements.",
              },
              {
                title: "3. Orders & Delivery",
                body: "Manufacturing and delivery timelines are estimates and may vary because of raw material availability, logistics or unforeseen circumstances.",
              },
              {
                title: "4. Warranty",
                body: "Warranty applies only as mentioned in the official quotation, invoice or purchase agreement. Damage caused by improper installation, misuse or unauthorized modification is not covered.",
              },
              {
                title: "5. Intellectual Property",
                body: "All product photographs, catalogues, logos, technical documents and website content remain the intellectual property of ITL Technical Services and may not be copied or reproduced without written permission.",
              },
              {
                title: "6. Limitation of Liability",
                body: "ITL Technical Services shall not be liable for indirect, incidental or consequential losses resulting from the use of our products or information published on this website.",
              },
              {
                title: "7. Privacy",
                body: "Information submitted through enquiry forms will only be used for business communication, quotation preparation and customer support. We do not sell customer information to third parties.",
              },
              {
                title: "8. Governing Law",
                body: "These Terms & Conditions shall be governed by the laws of India. Any disputes shall be subject to the jurisdiction of the competent courts.",
              },
            ].map((item) => (
              <Box
                key={item.title}
                sx={{
                  py: 4,
                  borderTop: "1px solid rgba(22,22,22,.08)",
                }}
              >
                <Typography
                  sx={{
                    fontFamily: "Cabinet Grotesk, system-ui, sans-serif",
                    fontWeight: 700,
                    fontSize: "1.45rem",
                    color: "#161616",
                    mb: 1.5,
                  }}
                >
                  {item.title}
                </Typography>

                <Typography
                  sx={{
                    color: "rgba(22,22,22,.65)",
                    fontSize: "1rem",
                    lineHeight: 1.9,
                  }}
                >
                  {item.body}
                </Typography>
              </Box>
            ))}

            <Typography
              sx={{
                mt: 6,
                pt: 3,
                borderTop: "1px solid rgba(22,22,22,.08)",
                fontFamily: "monospace",
                fontSize: ".82rem",
                letterSpacing: ".06em",
                color: "rgba(22,22,22,.45)",
              }}
            >
              LAST UPDATED • {new Date().toLocaleDateString()}
            </Typography>
          </Box>
        </ModalDialog>
      </Modal>
    </Box>
  );
}

export default Footer;
